import React, { useEffect, useState, useCallback } from 'react';
import { Form, Button, Input, Select, Spin, DatePicker, InputNumber, Typography, Tooltip, TimePicker, Popconfirm, Row, Col } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import weekday from 'dayjs/plugin/weekday';

import { PiExam, PiStudent, PiPlusCircle, PiTrash, PiBuildingOffice } from "react-icons/pi";
import { IoMdInformationCircle } from "react-icons/io";
import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/CreateAcademicTerm.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekday);

const { RangePicker } = DatePicker;
const { Text, Link: AntLink } = Typography;
const { Option } = Select;
const TIME_FORMAT = 'HH:mm';

const OFFICE_HOUR_START = 8;
const OFFICE_HOUR_END = 17;

// Helper to safely create a dayjs object
const safeDayjs = (dateInput, format) => {
    if (!dateInput) return null;
    if (dayjs.isDayjs(dateInput) && dateInput.isValid()) return dateInput;
    const d = format ? dayjs(dateInput, format, true) : dayjs(dateInput);
    return d.isValid() ? d : null;
};

const CreateAcademicTerm = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const location = useLocation();
    const [form] = Form.useForm();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(false);

    const [masterTimeSlots, setMasterTimeSlots] = useState([]);
    const [dayScheduleStructures, setDayScheduleStructures] = useState([]);

    const [availableRooms, setAvailableRooms] = useState([]);
    const [loadingRooms, setLoadingRooms] = useState(false);

    const [academicYearDateConstraint, setAcademicYearDateConstraint] = useState({ minDate: null, maxDate: null });
    const [isAcademicYearValidForDatePickers, setIsAcademicYearValidForDatePickers] = useState(false);
    const [activeAcademicYearDisplay, setActiveAcademicYearDisplay] = useState("");
    
    const [isEnrollmentPeriodSet, setIsEnrollmentPeriodSet] = useState(false);

    const isViewMode = location.pathname.includes('/view/');
    const isEditMode = !!id && !isViewMode;
    const isCreateMode = !id;

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleBack = () => navigate('/admin/manage-enrollment-period');

    useEffect(() => {
        const fetchRooms = async () => {
            if (isViewMode && !isEditMode && !id) return;
            setLoadingRooms(true);
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                const response = await fetch(`${apiUrl}/api/admin/rooms`);
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
                    throw new Error(errorData.message || `Failed to fetch rooms (HTTP ${response.status})`);
                }
                const result = await response.json();
                if (result.success && Array.isArray(result.data)) {
                    setAvailableRooms(result.data);
                } else {
                    toast.error(result.message || 'Could not load available rooms data format.');
                    setAvailableRooms([]);
                }
            } catch (error) {
                console.error("Error fetching rooms:", error);
                toast.error(`Error fetching rooms: ${error.message}`);
                setAvailableRooms([]);
            } finally {
                setLoadingRooms(false);
            }
        };
        fetchRooms();
    }, [isViewMode, isEditMode, id]);

    const checkForMasterSlotConflicts = (masterSlots, rooms) => {
        const getRoomName = (roomId) => {
            const room = rooms.find(r => r._id === roomId);
            return room ? room.name : `Unknown Room (ID: ${roomId})`;
        };

        if (!masterSlots || masterSlots.length < 2) return null;

        for (let i = 0; i < masterSlots.length; i++) {
            for (let j = i + 1; j < masterSlots.length; j++) {
                const slotA = masterSlots[i];
                const slotB = masterSlots[j];

                if (slotA.roomId && slotA.roomId === slotB.roomId) {
                    const startA = safeDayjs(slotA.startTime);
                    const endA = safeDayjs(slotA.endTime);
                    const startB = safeDayjs(slotB.startTime);
                    const endB = safeDayjs(slotB.endTime);

                    if (startA && endA && startB && endB) {
                        const isOverlapping = startA.isBefore(endB) && startB.isBefore(endA);
                        if (isOverlapping) {
                            const roomName = getRoomName(slotA.roomId);
                            return `Conflict in Master Template for room "${roomName}": Slot ${startA.format(TIME_FORMAT)}-${endA.format(TIME_FORMAT)} overlaps with ${startB.format(TIME_FORMAT)}-${endB.format(TIME_FORMAT)}.`;
                        }
                    }
                }
            }
        }
        return null;
    };
    
    const applyMasterSlotsToWeekdays = useCallback((masterSlots_param) => {
        const currentMasterSlots = masterSlots_param !== undefined ? masterSlots_param : masterTimeSlots;
        const currentFormSchedules = form.getFieldValue('dailyExamSchedules') || [];

        const masterConflictError = checkForMasterSlotConflicts(currentMasterSlots, availableRooms);
        if (masterConflictError) {
            toast.error(masterConflictError);
            return false;
        }

        if (currentFormSchedules.length === 0 && currentMasterSlots.length > 0) {
            if (masterSlots_param === undefined) toast.info("No days generated in the exam period to apply slots to.");
            return false;
        }

        if (currentMasterSlots.length === 0) {
            const newFormDailySchedules = currentFormSchedules.map((daySchedule) => {
                const structure = dayScheduleStructures.find(s => s.key === daySchedule.key) || {};
                if (!structure.isWeekend) {
                    return { ...daySchedule, timeSlots: [] };
                }
                return daySchedule;
            });
            form.setFieldsValue({ dailyExamSchedules: newFormDailySchedules });
            return true;
        }

        for (const slot of currentMasterSlots) {
            const sTime = dayjs.isDayjs(slot.startTime) && slot.startTime.isValid() ? slot.startTime : null;
            const eTime = dayjs.isDayjs(slot.endTime) && slot.endTime.isValid() ? slot.endTime : null;

            if (!sTime || !eTime || !slot.roomId || slot.maxCapacity === undefined || slot.maxCapacity === null) {
                toast.error("All master time slots must have valid start time, end time, an assigned room, and max capacity.");
                return false;
            }
            if (eTime.isSameOrBefore(sTime)) {
                toast.error(`Master slot error: End time (${eTime.format(TIME_FORMAT)}) must be after start time (${sTime.format(TIME_FORMAT)}).`);
                return false;
            }
            if (sTime.hour() < OFFICE_HOUR_START || eTime.hour() > OFFICE_HOUR_END || (eTime.hour() === OFFICE_HOUR_END && eTime.minute() > 0)) {
                toast.error(`Master slot (${sTime.format(TIME_FORMAT)}-${eTime.format(TIME_FORMAT)}) is outside office hours.`);
                return false;
            }
            const selectedRoom = availableRooms.find(r => r._id === slot.roomId);
            if (!selectedRoom) {
                toast.error(`Master slot error: Room with ID ${slot.roomId} not found or not loaded. Please select a valid room.`);
                return false;
            }
            if (slot.maxCapacity > selectedRoom.capacity) {
                toast.error(`Master slot in room "${selectedRoom.name}" has max capacity (${slot.maxCapacity}) exceeding room capacity (${selectedRoom.capacity}).`);
                return false;
            }
        }

        const newFormDailySchedules = currentFormSchedules.map((daySchedule) => {
            const structure = dayScheduleStructures.find(s => s.key === daySchedule.key) || {};
            if (structure && !structure.isWeekend) {
                return {
                    ...daySchedule,
                    timeSlots: currentMasterSlots.map(ms => ({
                        startTime: ms.startTime,
                        endTime: ms.endTime,
                        roomId: ms.roomId,
                        maxCapacity: ms.maxCapacity,
                        status: ms.status,
                        key: `slot-${daySchedule.key}-${Math.random().toString(16).slice(2)}`
                    }))
                };
            }
            return daySchedule;
        });

        form.setFieldsValue({ dailyExamSchedules: newFormDailySchedules });
        if (masterSlots_param === undefined) {
            toast.success("Master time slot configuration applied to all applicable weekdays.");
        }
        return true;
    }, [masterTimeSlots, form, dayScheduleStructures, availableRooms]);

    const handleAddMasterTimeSlot = () => {
        setMasterTimeSlots([
            ...masterTimeSlots,
            { startTime: null, endTime: null, roomId: null, maxCapacity: 10, status: 'Available', key: `master-${Date.now()}-${masterTimeSlots.length}` }
        ]);
    };

    const handleRemoveMasterTimeSlot = (indexToRemove) => {
        const newMasterSlots = masterTimeSlots.filter((_, index) => index !== indexToRemove);
        setMasterTimeSlots(newMasterSlots);
        if (form.getFieldValue('examOverallPeriod') && (form.getFieldValue('dailyExamSchedules') || []).length > 0) {
            const success = applyMasterSlotsToWeekdays(newMasterSlots);
            if (success) {
                toast.info(newMasterSlots.length === 0 ? "Master template cleared. Slots removed from weekdays." : "Master slot removed; template re-applied.");
            }
        }
    };

    const handleMasterTimeSlotChange = (index, field, value) => {
        const newMasterSlots = [...masterTimeSlots];
        if (field === 'startTime') {
            const endTime = newMasterSlots[index].endTime;
            if (endTime && dayjs.isDayjs(endTime) && endTime.isSameOrBefore(value)) {
                newMasterSlots[index].endTime = null;
            }
        }
        newMasterSlots[index][field] = value;
        setMasterTimeSlots(newMasterSlots);
    };

    const updateDateConstraintsBasedOnAcademicYear = useCallback((academicYearString) => {
        let newMinDate = null, newMaxDate = null, isValidForPickers = false;
        if (academicYearString && /^\d{4}-\d{4}$/.test(academicYearString)) {
            const [startY, endY] = academicYearString.split('-').map(Number);
            if (!isNaN(startY) && !isNaN(endY) && endY === startY + 1) {
                newMinDate = safeDayjs(`${startY}-01-01`);
                newMaxDate = safeDayjs(`${endY}-12-31`);
                if (newMinDate && newMaxDate) isValidForPickers = true;
            }
        }
        setAcademicYearDateConstraint({ minDate: newMinDate, maxDate: newMaxDate });
        setIsAcademicYearValidForDatePickers(isValidForPickers);
        setActiveAcademicYearDisplay(
            isValidForPickers && isCreateMode ? `Creating term for AY: ${academicYearString}. Dates constrained.` :
                !isValidForPickers && isCreateMode ? "No active/valid AY. Term creation needs an active AY." : ""
        );

        const clearPicker = (fieldName, friendlyName, clearSlots = false) => {
            form.setFieldsValue({ [fieldName]: null });
            if (clearSlots && fieldName === 'examOverallPeriod') {
                setDayScheduleStructures([]);
                form.setFieldsValue({ dailyExamSchedules: [] });
            }
            if (!isViewMode && academicYearString !== null) toast.info(`${friendlyName} cleared due to invalid/empty AY or AY change.`);
        };

        const clearRangePickerIfInvalid = (fieldName, fieldUserFriendlyName, isExamOverall = false) => {
            const currentPeriodValues = form.getFieldValue(fieldName);
            if (currentPeriodValues && currentPeriodValues.length === 2 && currentPeriodValues.every(d => dayjs.isDayjs(d) && d.isValid())) {
                const [currentStart, currentEnd] = currentPeriodValues;
                if (isValidForPickers && newMinDate && newMaxDate) {
                    if (!currentStart.isBetween(newMinDate, newMaxDate, 'day', '[]') || !currentEnd.isBetween(newMinDate, newMaxDate, 'day', '[]')) {
                        clearPicker(fieldName, fieldUserFriendlyName, isExamOverall);
                        if (!isViewMode) toast.info(`${fieldUserFriendlyName} cleared, was outside new AY range.`);
                    }
                } else {
                    clearPicker(fieldName, fieldUserFriendlyName, isExamOverall);
                }
            } else if (!isValidForPickers && currentPeriodValues && currentPeriodValues.length === 2) {
                clearPicker(fieldName, fieldUserFriendlyName, isExamOverall);
            }
        };
        clearRangePickerIfInvalid('examOverallPeriod', 'Overall Exam Period', true);
        clearRangePickerIfInvalid('enrollmentPeriod', 'Enrollment Period');
    }, [form, isCreateMode, isViewMode]);

    useEffect(() => {
        const mapApiDataToForm = (data) => {
            const formData = {
                academicYear: data.academicYear,
                term: data.term,
                status: data.status,
                isArchived: data.isArchived || false,
            };
            updateDateConstraintsBasedOnAcademicYear(data.academicYear || null);

            if (data.enrollmentStartDate && data.enrollmentEndDate) {
                const enrollStart = safeDayjs(data.enrollmentStartDate);
                const enrollEnd = safeDayjs(data.enrollmentEndDate);
                if (enrollStart && enrollEnd) {
                    formData.enrollmentPeriod = [enrollStart, enrollEnd];
                    setIsEnrollmentPeriodSet(true); 
                }
            }
            if (data.examOverallStartDate && data.examOverallEndDate) {
                const examStart = safeDayjs(data.examOverallStartDate);
                const examEnd = safeDayjs(data.examOverallEndDate);
                if (examStart && examEnd) formData.examOverallPeriod = [examStart, examEnd];
            }

            const newDayStructures = [];
            if (data.dailyExamSchedules && data.dailyExamSchedules.length > 0) {
                formData.dailyExamSchedules = data.dailyExamSchedules
                    .map(daySchedule => {
                        if (!daySchedule.date) {
                            console.warn("Daily schedule from API missing date:", daySchedule);
                            return null;
                        }
                        const scheduleDateDayjs = safeDayjs(daySchedule.date);
                        if (!scheduleDateDayjs) {
                            console.warn("Invalid date in API dailySchedule:", daySchedule.date);
                            return null;
                        }
                        const dayItemKey = daySchedule._id || scheduleDateDayjs.format('YYYYMMDD') + Math.random().toString(16).slice(5);
                        newDayStructures.push({
                            date: scheduleDateDayjs,
                            isWeekend: scheduleDateDayjs.day() === 0 || scheduleDateDayjs.day() === 6,
                            key: dayItemKey
                        });
                        return {
                            _id: daySchedule._id,
                            date: scheduleDateDayjs,
                            key: dayItemKey,
                            timeSlots: (daySchedule.timeSlots || []).map(slot => ({
                                _id: slot._id,
                                startTime: safeDayjs(slot.startTime, TIME_FORMAT),
                                endTime: safeDayjs(slot.endTime, TIME_FORMAT),
                                roomId: slot.roomId?._id || slot.roomId,
                                maxCapacity: slot.maxCapacity,
                                status: slot.status,
                                key: slot._id || `slot-${dayItemKey}-${Math.random().toString(16).slice(5)}`
                            }))
                        };
                    }).filter(Boolean);
            } else {
                formData.dailyExamSchedules = [];
            }
            setDayScheduleStructures(newDayStructures);
            return formData;
        };

        const fetchTermDataForEditView = async () => {
            setInitialDataLoading(true); setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/terms/${id}`);
                const result = await response.json();
                if (response.ok && result.data) {
                    const formData = mapApiDataToForm(result.data);
                    form.setFieldsValue(formData);
                } else {
                    toast.error(result.message || 'Failed to load term data.');
                    navigate('/admin/manage-enrollment-period');
                }
            } catch (error) {
                console.error('Network error fetching term:', error);
                toast.error('Network error. Could not fetch term data.');
                navigate('/admin/manage-enrollment-period');
            } finally {
                setInitialDataLoading(false); setLoading(false);
            }
        };

        const setupFormForCreate = async () => {
            setInitialDataLoading(true);
            form.setFieldsValue({
                status: 'Active',
                dailyExamSchedules: [],
                isArchived: false,
                term: '',
                enrollmentPeriod: null,
                examOverallPeriod: null,
                academicYear: null
            });
            setIsEnrollmentPeriodSet(false); 
            setDayScheduleStructures([]);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/years?status=Active&isArchived=false&limit=1`);
                const result = await response.json();
                if (response.ok && result.data && result.data.length > 0 && result.data[0].Year) {
                    form.setFieldsValue({ academicYear: result.data[0].Year });
                    updateDateConstraintsBasedOnAcademicYear(result.data[0].Year);
                } else {
                    toast.warn('No active academic year found. An active AY is required to create/edit terms.');
                    updateDateConstraintsBasedOnAcademicYear(null);
                }
            } catch (error) {
                console.error('Error fetching active academic year:', error);
                toast.error('Error fetching active AY. Ensure one exists.');
                updateDateConstraintsBasedOnAcademicYear(null);
            } finally {
                setInitialDataLoading(false);
            }
        };

        if (id) fetchTermDataForEditView();
        else setupFormForCreate();
    }, [id, form, navigate, updateDateConstraintsBasedOnAcademicYear]);

    const checkForTimeSlotConflicts = (dailySchedules, rooms) => {
        const getRoomName = (roomId) => {
            const room = rooms.find(r => r._id === roomId);
            return room ? room.name : `Unknown Room (ID: ${roomId})`;
        };

        for (const daySchedule of dailySchedules) {
            const slots = daySchedule.timeSlots;
            if (!slots || slots.length < 2) continue;

            for (let i = 0; i < slots.length; i++) {
                for (let j = i + 1; j < slots.length; j++) {
                    const slotA = slots[i];
                    const slotB = slots[j];

                    if (slotA.roomId === slotB.roomId) {
                        const startA = dayjs(slotA.startTime, TIME_FORMAT);
                        const endA = dayjs(slotA.endTime, TIME_FORMAT);
                        const startB = dayjs(slotB.startTime, TIME_FORMAT);
                        const endB = dayjs(slotB.endTime, TIME_FORMAT);

                        if (!startA.isValid() || !endA.isValid() || !startB.isValid() || !endB.isValid()) {
                            console.warn(`Invalid time found during conflict check on ${daySchedule.date}. Skipping pair.`);
                            continue;
                        }

                        const isOverlapping = startA.isBefore(endB) && startB.isBefore(endA);

                        if (isOverlapping) {
                            const dateFormatted = dayjs(daySchedule.date).format('MMM D, YYYY');
                            const roomName = getRoomName(slotA.roomId);
                            return `Conflict on ${dateFormatted} in room "${roomName}": Slot ${slotA.startTime}-${slotA.endTime} overlaps with ${slotB.startTime}-${slotB.endTime}.`;
                        }
                    }
                }
            }
        }
        return null;
    };


    const handleSubmit = async (values) => {
        if (isViewMode) return;
        setLoading(true);

        const currentAcademicYear = form.getFieldValue('academicYear');
        if (!currentAcademicYear || !/^\d{4}-\d{4}$/.test(currentAcademicYear)) {
            toast.error("A valid Academic Year (YYYY-YYYY) is required.");
            setLoading(false); return;
        }
        let tempMinDate, tempMaxDate;
        const [startAYStr, endAYStr] = currentAcademicYear.split('-');
        tempMinDate = safeDayjs(`${startAYStr}-01-01`);
        tempMaxDate = safeDayjs(`${endAYStr}-12-31`);

        if (!values.enrollmentPeriod || values.enrollmentPeriod.length !== 2 || !values.enrollmentPeriod.every(d => dayjs.isDayjs(d) && d.isValid())) {
            toast.error("Enrollment Period is required and must contain valid dates."); setLoading(false); return;
        }
        const [enrollStart, enrollEnd] = values.enrollmentPeriod;
        if (enrollEnd.isBefore(enrollStart, 'day')) {
            toast.error("Enrollment end date must be on or after start date."); setLoading(false); return;
        }
        if (!validatePeriodAgainstAY(values.enrollmentPeriod, 'Enrollment Period', tempMinDate, tempMaxDate)) {
            setLoading(false); return;
        }

        const payload = {
            academicYear: currentAcademicYear,
            term: values.term.trim(),
            status: values.status,
            enrollmentStartDate: enrollStart.format('YYYY-MM-DD'),
            enrollmentEndDate: enrollEnd.format('YYYY-MM-DD'),
            isArchived: values.isArchived || false,
            examOverallStartDate: null,
            examOverallEndDate: null,
            dailyExamSchedules: []
        };

        if (values.examOverallPeriod && values.examOverallPeriod.length === 2 && values.examOverallPeriod.every(d => dayjs.isDayjs(d) && d.isValid())) {
            const [examStart, examEnd] = values.examOverallPeriod;
            if (examEnd.isBefore(examStart, 'day')) {
                toast.error("Overall exam period end date must be on or after start date."); setLoading(false); return;
            }
            if (!validatePeriodAgainstAY(values.examOverallPeriod, 'Overall Exam Period', tempMinDate, tempMaxDate)) {
                setLoading(false); return;
            }
            if (examStart.isBefore(enrollStart, 'day') || examEnd.isAfter(enrollEnd, 'day')) {
                toast.error("Overall Exam Period must be within the Enrollment Period."); setLoading(false); return;
            }
            payload.examOverallStartDate = examStart.format('YYYY-MM-DD');
            payload.examOverallEndDate = examEnd.format('YYYY-MM-DD');

            if (values.dailyExamSchedules && values.dailyExamSchedules.length > 0) {
                for (const daySchedule of values.dailyExamSchedules) {
                    if (!daySchedule || !daySchedule.date || !dayjs.isDayjs(daySchedule.date) || !daySchedule.date.isValid()) {
                        console.warn("Skipping daySchedule with invalid/missing date object in handleSubmit loop:", daySchedule);
                        continue;
                    }
                    const scheduleDate = daySchedule.date;

                    if (!scheduleDate.isBetween(examStart, examEnd, 'day', '[]')) {
                        toast.error(`Scheduled day ${scheduleDate.format('YYYY-MM-DD')} is outside the Overall Exam Period.`);
                        setLoading(false); return;
                    }

                    const processedTimeSlots = [];
                    if (daySchedule.timeSlots && daySchedule.timeSlots.length > 0) {
                        for (const slot of daySchedule.timeSlots) {
                            const sTime = dayjs.isDayjs(slot.startTime) && slot.startTime.isValid() ? slot.startTime : null;
                            const eTime = dayjs.isDayjs(slot.endTime) && slot.endTime.isValid() ? slot.endTime : null;

                            if (!sTime || !eTime || !slot.roomId || slot.maxCapacity === undefined || slot.maxCapacity === null) {
                                toast.error(`Incomplete data for a time slot on ${scheduleDate.format('YYYY-MM-DD')}. Start, end, room, and capacity are required.`);
                                setLoading(false); return;
                            }

                            if (eTime.isSameOrBefore(sTime)) {
                                toast.error(`Invalid time slot ${sTime.format(TIME_FORMAT)}-${eTime.format(TIME_FORMAT)} on ${scheduleDate.format('YYYY-MM-DD')}.`);
                                setLoading(false); return;
                            }
                            if (sTime.hour() < OFFICE_HOUR_START || eTime.hour() > OFFICE_HOUR_END || (eTime.hour() === OFFICE_HOUR_END && eTime.minute() > 0)) {
                                toast.error(`Time slot on ${scheduleDate.format('YYYY-MM-DD')} (${sTime.format(TIME_FORMAT)}-${eTime.format(TIME_FORMAT)}) is outside office hours.`);
                                setLoading(false); return;
                            }
                            const selectedRoom = availableRooms.find(r => r._id === slot.roomId);
                            if (!selectedRoom) {
                                toast.error(`Invalid room (ID: ${slot.roomId}) selected for a time slot on ${scheduleDate.format('YYYY-MM-DD')}.`);
                                setLoading(false); return;
                            }
                            if (parseInt(slot.maxCapacity, 10) > selectedRoom.capacity) {
                                toast.error(`Slot in room "${selectedRoom.name}" on ${scheduleDate.format('YYYY-MM-DD')} for ${sTime.format(TIME_FORMAT)}-${eTime.format(TIME_FORMAT)} has max capacity (${slot.maxCapacity}) exceeding room capacity (${selectedRoom.capacity}).`);
                                setLoading(false); return;
                            }
                            processedTimeSlots.push({
                                startTime: sTime.format(TIME_FORMAT),
                                endTime: eTime.format(TIME_FORMAT),
                                roomId: slot.roomId,
                                maxCapacity: parseInt(slot.maxCapacity, 10),
                                status: slot.status || 'Available',
                                _id: slot._id
                            });
                        }
                    }
                    const daySchedulePayload = {
                        date: scheduleDate.format('YYYY-MM-DD'),
                        timeSlots: processedTimeSlots
                    };
                    if (daySchedule._id) {
                        daySchedulePayload._id = daySchedule._id;
                    }

                    const dayStruct = dayScheduleStructures.find(ds => ds.key === daySchedule.key);
                    if (processedTimeSlots.length > 0 || (daySchedule.timeSlots?.length === 0 && dayStruct && !dayStruct.isWeekend)) {
                        payload.dailyExamSchedules.push(daySchedulePayload);
                    }
                }
            }
        } else if (values.dailyExamSchedules && values.dailyExamSchedules.some(ds => ds && ds.timeSlots && ds.timeSlots.length > 0)) {
            toast.error("Overall Exam Period must be set if configuring daily exam slots.");
            setLoading(false); return;
        }

        if (payload.dailyExamSchedules && payload.dailyExamSchedules.length > 0) {
            const conflictError = checkForTimeSlotConflicts(payload.dailyExamSchedules, availableRooms);
            if (conflictError) {
                toast.error(conflictError);
                setLoading(false);
                return;
            }
        }

        if (values.hasOwnProperty('isArchived')) {
            payload.isArchived = !!values.isArchived;
        }

        const url = isEditMode ? `${process.env.REACT_APP_API_URL}/api/admin/terms/${id}` : `${process.env.REACT_APP_API_URL}/api/admin/terms`;
        const method = isEditMode ? 'PATCH' : 'POST';

        try {
            const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const data = await response.json();
            if (response.ok) {
                toast.success(`Academic Term ${isEditMode ? 'updated' : 'created'} successfully.`);
                navigate('/admin/manage-enrollment-period');
            } else {
                const errorMsg = (data.errors && Array.isArray(data.errors)) ? data.errors.join(' ') : (data.message || data.error || `Failed to ${isEditMode ? 'update' : 'create'} term.`);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Submission error:', error);
            toast.error(error.message || 'An error occurred during submission.');
        } finally {
            setLoading(false);
        }
    };

    const validatePeriodAgainstAY = (period, periodName, minAYDate, maxAYDate) => {
        if (!period || period.length !== 2 || !period.every(d => dayjs.isDayjs(d) && d.isValid())) {
            if (!isViewMode) toast.error(`${periodName} contains invalid or incomplete dates.`);
            return false;
        }
        const [pStart, pEnd] = period;

        if (minAYDate && maxAYDate && minAYDate.isValid() && maxAYDate.isValid()) {
            if (pStart.isBefore(minAYDate, 'day') || pEnd.isAfter(maxAYDate, 'day')) {
                toast.error(`${periodName} must be within the selected Academic Year (${minAYDate.format('MMM DD, YYYY')} to ${maxAYDate.format('MMM DD, YYYY')}).`);
                return false;
            }
        } else {
            if (!isViewMode && form.getFieldValue('academicYear')) toast.error(`Academic Year is invalid. Cannot validate ${periodName.toLowerCase()} boundaries.`);
            else if (!isViewMode) toast.error(`Academic Year not set. Cannot validate ${periodName.toLowerCase()} boundaries.`)
            return false;
        }
        return true;
    };

    const handleExamOverallPeriodChange = (dates) => {
        if (isViewMode || !isAcademicYearValidForDatePickers) {
            if (!isAcademicYearValidForDatePickers && !isViewMode && form.getFieldValue('academicYear')) {
                toast.warn("Set a valid AY before defining exam periods.");
            }
            setDayScheduleStructures([]);
            form.setFieldsValue({ dailyExamSchedules: [] });
            return;
        }

        if (dates && (dates.length !== 2 || !dates.every(d => dayjs.isDayjs(d) && d.isValid()))) {
            if (!isViewMode) toast.error("Invalid dates selected for Overall Exam Period.");
            form.setFieldsValue({ dailyExamSchedules: [] });
            setDayScheduleStructures([]);
            return;
        }
        if (!dates || dates.length !== 2) {
            setDayScheduleStructures([]);
            form.setFieldsValue({ dailyExamSchedules: [] });
            return;
        }

        const [start, end] = dates;
        const enrollmentPeriodValues = form.getFieldValue('enrollmentPeriod');
        if (!enrollmentPeriodValues || enrollmentPeriodValues.length !== 2 || !enrollmentPeriodValues.every(d => dayjs.isDayjs(d) && d.isValid())) {
            toast.warn("Please set a valid Enrollment Period first.");
            form.setFieldsValue({ examOverallPeriod: null, dailyExamSchedules: [] });
            setDayScheduleStructures([]);
            return;
        }
        const [enrollStart, enrollEnd] = enrollmentPeriodValues;
        if (start.isBefore(enrollStart, 'day') || end.isAfter(enrollEnd, 'day')) {
            toast.error("Overall Exam Period must be within the Enrollment Period.");
            form.setFieldsValue({ examOverallPeriod: null, dailyExamSchedules: [] });
            setDayScheduleStructures([]);
            return;
        }

        let currentDate = start.clone();
        const newDayStructs = [];
        const newFormSchedules = [];
        const existingSchedules = form.getFieldValue('dailyExamSchedules') || [];

        while (currentDate.isSameOrBefore(end, 'day')) {
            const dayOfWeek = currentDate.day();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            const existingDayData = existingSchedules.find(es => es.date && dayjs.isDayjs(es.date) && es.date.isSame(currentDate, 'day'));
            const dayKey = existingDayData?.key || currentDate.valueOf() + Math.random().toString(16).slice(2);

            newDayStructs.push({ date: currentDate.clone(), isWeekend: isWeekend, key: dayKey });
            newFormSchedules.push({
                _id: existingDayData?._id,
                date: currentDate.clone(),
                key: dayKey,
                timeSlots: isWeekend ? [] : (existingDayData?.timeSlots || [])
            });
            currentDate = currentDate.add(1, 'day');
        }
        setDayScheduleStructures(newDayStructs);
        form.setFieldsValue({ dailyExamSchedules: newFormSchedules });
    };

    const disabledTime = () => {
        const disabledHours = () => {
            const hours = [];
            for (let i = 0; i < 24; i++) { if (i < OFFICE_HOUR_START || i >= OFFICE_HOUR_END) { hours.push(i); } }
            return hours;
        };
        return { disabledHours };
    };

    const getDisabledMasterEndTimes = (startTime) => {
        const officeHoursConfig = disabledTime();
        if (!startTime || !dayjs.isDayjs(startTime) || !startTime.isValid()) {
            return officeHoursConfig;
        }

        const startHour = startTime.hour();
        const startMinute = startTime.minute();
        
        const disabledHours = () => {
            const baseDisabled = officeHoursConfig.disabledHours();
            for (let i = 0; i < startHour; i++) {
                if (!baseDisabled.includes(i)) {
                    baseDisabled.push(i);
                }
            }
            return baseDisabled;
        };

        const disabledMinutes = (selectedHour) => {
            if (selectedHour === startHour) {
                const minutes = [];
                for (let i = 0; i <= startMinute; i++) {
                    minutes.push(i);
                }
                return minutes;
            }
            return [];
        };

        return { disabledHours, disabledMinutes };
    };
    
    // ** THIS IS THE CORRECTED FUNCTION **
    const disabledExamPeriodDates = (current) => {
        if (isViewMode) return false;
        if (!current || !dayjs.isDayjs(current)) return true;
        // ** THE FIX IS HERE **
        // In create mode, disable any date before today.
        if (isCreateMode && current.isBefore(dayjs().startOf('day'))) {
            return true;
        }
        
        if (!isAcademicYearValidForDatePickers) return true;

        const minAYDate = academicYearDateConstraint.minDate;
        const maxAYDate = academicYearDateConstraint.maxDate;

        if (minAYDate && maxAYDate && minAYDate.isValid() && maxAYDate.isValid()) {
            if (!current.isBetween(minAYDate, maxAYDate, 'day', '[]')) {
                return true;
            }
        }
        const enrollmentPeriodValues = form.getFieldValue('enrollmentPeriod');
        if (enrollmentPeriodValues && enrollmentPeriodValues.length === 2 && enrollmentPeriodValues.every(d => dayjs.isDayjs(d) && d.isValid())) {
            const [enrollStart, enrollEnd] = enrollmentPeriodValues;
            if (!current.isBetween(enrollStart, enrollEnd, 'day', '[]')) { return true; }
        } else { return true; }
        return false;
    };
    
    // ** THIS IS THE CORRECTED FUNCTION **
    const disabledEnrollmentDates = (current) => {
        if (isViewMode) return false;
        if (!current || !dayjs.isDayjs(current)) return true;
        // ** THE FIX IS HERE **
        // In create mode, disable any date before today.
        if (isCreateMode && current.isBefore(dayjs().startOf('day'))) {
            return true;
        }

        const minAYDate = academicYearDateConstraint.minDate;
        const maxAYDate = academicYearDateConstraint.maxDate;

        if (isAcademicYearValidForDatePickers && minAYDate && maxAYDate && minAYDate.isValid() && maxAYDate.isValid()) {
            if (!current.isBetween(minAYDate, maxAYDate, 'day', '[]')) {
                return true;
            }
        } else if (!isAcademicYearValidForDatePickers && !isViewMode) { return true; }
        return false;
    };

    let pageTitle = isEditMode ? 'Edit Academic Term' : isViewMode ? 'View Academic Term' : 'Create Academic Term';

    const handleRoomChangeForSlot = (dayIndex, slotIndex, roomId) => {
        const schedules = form.getFieldValue('dailyExamSchedules') || [];
        const newSchedules = schedules.map(day => ({
            ...day,
            timeSlots: day.timeSlots ? day.timeSlots.map(slot => ({ ...slot })) : []
        }));

        if (newSchedules[dayIndex] && newSchedules[dayIndex].timeSlots && newSchedules[dayIndex].timeSlots[slotIndex]) {
            newSchedules[dayIndex].timeSlots[slotIndex].roomId = roomId;
            form.setFieldsValue({ dailyExamSchedules: newSchedules });
            form.validateFields([['dailyExamSchedules', dayIndex, 'timeSlots', slotIndex, 'maxCapacity']]);
        } else {
            console.error("Could not find slot to update room for:", dayIndex, slotIndex, "Schedules:", newSchedules);
        }
    };

    const getRoomNameById = (roomId) => {
        if (!roomId || availableRooms.length === 0) return 'N/A';
        const room = availableRooms.find(r => r._id === roomId);
        return room ? room.name : `Unknown (ID: ${String(roomId).slice(-4)})`;
    };

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack}><MdOutlineKeyboardArrowLeft /></div>
                        <p className="heading">{pageTitle}</p>
                    </div>
                    {initialDataLoading && <Spin tip="Loading data..." size="large" style={{ display: 'block', margin: '20px auto' }} />}

                    {!initialDataLoading && (
                        <Form
                            form={form}
                            layout="horizontal"
                            labelCol={{ xs: { span: 24 }, sm: { span: 7 } }}
                            wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                            onFinish={handleSubmit}
                        >
                            <div className="container-columns">
                                <div className="column">
                                    {/* TERM DETAILS */}
                                    <div className="group-title"><IoMdInformationCircle className="section-icon" /> <p className="section-title">TERM DETAILS</p></div>
                                    <Form.Item label="Academic Year" name="academicYear" rules={[{ required: true, message: 'Active AY required.' }, { pattern: /^\d{4}-\d{4}$/, message: 'Format YYYY-YYYY' }]} tooltip={isCreateMode ? activeAcademicYearDisplay : "Term's Academic Year."}>
                                        <Input placeholder={isCreateMode ? "Auto-set from Active AY" : "Academic Year"} disabled readOnly />
                                    </Form.Item>
                                    <Form.Item label="Term Name" name="term" rules={isViewMode ? [] : [{ required: true, message: "Term name is required." }, { type: 'string', max: 100 }]}>
                                        <Input placeholder="e.g. 1st, 2nd, Summer" disabled={isViewMode || loading} maxLength={100} />
                                    </Form.Item>
                                    <Form.Item label="Status" name="status" rules={isViewMode ? [] : [{ required: true, message: "Status is required." }]}>
                                        <Select placeholder="Select status" disabled={isViewMode || loading} defaultValue="Active">
                                            <Select.Option value="Active">Active</Select.Option> <Select.Option value="Inactive">Inactive</Select.Option>
                                        </Select>
                                    </Form.Item>
                                    {(isEditMode || isViewMode) &&
                                        <Form.Item label="Archived" name="isArchived" valuePropName="checked">
                                            <Select disabled={isViewMode || loading} placeholder="Select archive status" defaultValue={false}>
                                                <Select.Option value={false}>No</Select.Option>
                                                <Select.Option value={true}>Yes</Select.Option>
                                            </Select>
                                        </Form.Item>}

                                    {/* ENROLLMENT PERIOD */}
                                    <div className="group-title" style={{ marginTop: '20px' }}><PiStudent className="section-icon" /> <p className="section-title">ENROLLMENT PERIOD</p></div>
                                    <Form.Item label="Enrollment Period" name="enrollmentPeriod" rules={isViewMode ? [] : [{ required: true, message: 'Enrollment period required.' }, { validator: (_, val) => { if (!val || val.length !== 2 || !val.every(d => dayjs.isDayjs(d) && d.isValid())) return Promise.resolve(); return (val[1].isBefore(val[0], 'day')) ? Promise.reject(new Error('End date must be on or after start date!')) : Promise.resolve(); } }]} tooltip={!isAcademicYearValidForDatePickers && !isViewMode ? "Set a valid Academic Year first." : "Dates for student enrollment."}>
                                        <RangePicker 
                                            style={{ width: '100%' }} 
                                            format="YYYY-MM-DD" 
                                            disabled={isViewMode || loading || !isAcademicYearValidForDatePickers} 
                                            disabledDate={disabledEnrollmentDates}
                                            onChange={(dates) => {
                                                const isPeriodValid = dates && dates.length === 2 && dates.every(d => dayjs.isDayjs(d) && d.isValid());
                                                setIsEnrollmentPeriodSet(isPeriodValid);

                                                if (isViewMode || loading) return;

                                                const examPeriod = form.getFieldValue('examOverallPeriod');
                                                if (examPeriod && examPeriod.length === 2 && examPeriod.every(d => dayjs.isDayjs(d) && d.isValid())) {
                                                    if (!isPeriodValid) {
                                                        if (!isViewMode) toast.info("Enrollment period cleared. Overall Exam Period and slots also cleared.");
                                                        form.setFieldsValue({ examOverallPeriod: null, dailyExamSchedules: [] });
                                                        setDayScheduleStructures([]);
                                                    } else {
                                                        const [enrollStart, enrollEnd] = dates;
                                                        const [examStart, examEnd] = examPeriod;
                                                        if (examStart.isBefore(enrollStart, 'day') || examEnd.isAfter(enrollEnd, 'day')) {
                                                            if (!isViewMode) toast.info("Enrollment period changed. Overall Exam Period was outside new range; cleared with slots.");
                                                            form.setFieldsValue({ examOverallPeriod: null, dailyExamSchedules: [] });
                                                            setDayScheduleStructures([]);
                                                        }
                                                    }
                                                }
                                                if (form.getFieldValue('examOverallPeriod')) {
                                                    form.validateFields(['examOverallPeriod']);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </div>

                                <div className="column">
                                    {/* EXAM CONFIGURATION */}
                                    <div className="group-title"><PiExam className="section-icon" /> <p className="section-title">EXAM CONFIGURATION (OPTIONAL)</p></div>
                                    <Form.Item label="Overall Exam Period" name="examOverallPeriod" rules={isViewMode ? [] : [{ validator: (_, val) => { if (!val || val.length !== 2 || !val.every(d => dayjs.isDayjs(d) && d.isValid())) return Promise.resolve(); return (val[1].isBefore(val[0], 'day')) ? Promise.reject(new Error('End date must be on or after start date!')) : Promise.resolve(); } }]} tooltip={!isEnrollmentPeriodSet && !isViewMode ? "Set valid Enrollment Period first." : "Optional: Define a window for exams."}>
                                        <RangePicker 
                                            style={{ width: '100%' }} 
                                            format="YYYY-MM-DD" 
                                            disabled={isViewMode || loading || !isAcademicYearValidForDatePickers || !isEnrollmentPeriodSet} 
                                            disabledDate={disabledExamPeriodDates} 
                                            onChange={handleExamOverallPeriodChange} 
                                            placeholder={["Start Date", "End Date"]} 
                                        />
                                    </Form.Item>

                                    {!isViewMode && form.getFieldValue('examOverallPeriod') && (
                                        <div className="master-slot-config-section">
                                            <Text strong style={{ marginBottom: '10px', display: 'block' }}>Master Time Slot Template <Tooltip title="Define a template for time slots (including room and capacity) to quickly apply to all weekdays within the exam period. You can further customize individual days."><IoMdInformationCircle style={{ marginLeft: 5, color: '#1890ff' }} /></Tooltip></Text>
                                            {masterTimeSlots.map((slot, index) => {
                                                const usedRoomIds = masterTimeSlots
                                                    .filter((_, i) => i !== index)
                                                    .map(s => s.roomId)
                                                    .filter(id => id);

                                                return (
                                                <div key={slot.key} className="time-slot-item master-slot-item">
                                                    <Row gutter={16} align="middle">
                                                        <Col xs={24} sm={12} md={8}>
                                                            <Form.Item label="Room" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} rules={[{ required: true, message: 'Room required.' }]}>
                                                                <Select placeholder="Select Room" style={{ width: '100%' }} value={slot.roomId} onChange={value => handleMasterTimeSlotChange(index, 'roomId', value)} loading={loadingRooms} showSearch filterOption={(input, option) => (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())}>
                                                                    {availableRooms.map(room => (
                                                                        <Option 
                                                                            key={room._id} 
                                                                            value={room._id} 
                                                                            disabled={usedRoomIds.includes(room._id)}
                                                                        >
                                                                            {room.name} (Cap: {room.capacity})
                                                                        </Option>
                                                                    ))}
                                                                </Select>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={12} sm={6} md={5}>
                                                            <Form.Item label="Start" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} rules={[{ required: true, message: 'Required' }]}>
                                                                <TimePicker style={{ width: '100%' }} format={TIME_FORMAT} minuteStep={15} disabledTime={disabledTime} value={safeDayjs(slot.startTime)} onChange={time => handleMasterTimeSlotChange(index, 'startTime', time)} />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={12} sm={6} md={5}>
                                                            <Form.Item label="End" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} rules={[{ required: true, message: 'Required' }, () => ({ validator(_, value) { const sTime = slot.startTime; if (!value || !sTime || !dayjs.isDayjs(sTime) || !dayjs.isDayjs(value) || value.isAfter(sTime)) return Promise.resolve(); return Promise.reject(new Error('End > Start!')); } })]}>
                                                                <TimePicker style={{ width: '100%' }} format={TIME_FORMAT} minuteStep={15} disabledTime={() => getDisabledMasterEndTimes(slot.startTime)} value={safeDayjs(slot.endTime)} onChange={time => handleMasterTimeSlotChange(index, 'endTime', time)} />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={12} sm={8} md={6}>
                                                            <Form.Item label="Slot Cap." labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0 }, { validator: async (_, value) => { if (slot.roomId && value !== undefined && value !== null) { const selectedRoom = availableRooms.find(r => r._id === slot.roomId); if (selectedRoom && value > selectedRoom.capacity) return Promise.reject(new Error(`Max ${selectedRoom.capacity}!`)); } return Promise.resolve(); } }]}>
                                                                <InputNumber style={{ width: '100%' }} min={0} value={slot.maxCapacity} onChange={val => handleMasterTimeSlotChange(index, 'maxCapacity', val)} placeholder={slot.roomId ? `Room Cap: ${availableRooms.find(r => r._id === slot.roomId)?.capacity || 'N/A'}` : "Select Room"} disabled={!slot.roomId} />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={12} sm={8} md={6}>
                                                            <Form.Item label="Status" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} rules={[{ required: true, message: 'Required' }]}>
                                                                <Select value={slot.status} onChange={val => handleMasterTimeSlotChange(index, 'status', val)} style={{ width: '100%' }}>
                                                                    <Select.Option value="Available">Available</Select.Option> <Select.Option value="Unavailable">Unavailable</Select.Option>
                                                                </Select>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={8} md={4} style={{ display: 'flex', alignItems: 'center', paddingTop: '8px', justifyContent: 'flex-start' }}>
                                                            <AntLink onClick={() => handleRemoveMasterTimeSlot(index)}><PiTrash style={{ color: 'red', fontSize: '1.4em' }} /></AntLink>
                                                        </Col>
                                                    </Row>
                                                </div>
                                                )
                                            })}
                                            <Button type="dashed" onClick={handleAddMasterTimeSlot} icon={<PiPlusCircle />} style={{ marginRight: 8, marginBottom: 10 }}>Add Master Slot</Button>
                                            <Button type="primary" onClick={() => applyMasterSlotsToWeekdays()} disabled={masterTimeSlots.length === 0 || (form.getFieldValue('dailyExamSchedules') || []).length === 0}>Apply Template</Button>
                                        </div>
                                    )}

                                    {/* DAILY TIME SLOT & ROOM CONFIGURATION */}
                                    <div className="group-title" style={{ marginTop: '20px', marginBottom: '5px' }}><PiBuildingOffice className="section-icon" /> <p className="section-title">DAILY TIME SLOT & ROOM CONFIGURATION</p></div>
                                    <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #f0f0f0', padding: '10px', borderRadius: '4px', background: ((form.getFieldValue('dailyExamSchedules') || []).length > 0) ? '#fff' : '#fafafa' }}>
                                        <Form.List name="dailyExamSchedules">
                                            {(dayFields) => {
                                                const currentFormSchedules = form.getFieldValue('dailyExamSchedules') || [];
                                                if (!form.getFieldValue('examOverallPeriod') && !isViewMode) { return <p className="form-list-placeholder">Set "Overall Exam Period" to configure daily time slots.</p>; }
                                                if (currentFormSchedules.length === 0 && isViewMode) { return <p className="form-list-placeholder">No exam/interview slots configured.</p>; }
                                                if (currentFormSchedules.length === 0 && !isViewMode && form.getFieldValue('examOverallPeriod')) { return <p className="form-list-placeholder">No days generated (check exam period).</p>; }

                                                return dayFields.map((dayField, dayIndex) => {
                                                    const dayScheduleData = currentFormSchedules[dayIndex] || {};
                                                    const dayScheduleDateDayjs = dayjs.isDayjs(dayScheduleData.date) ? dayScheduleData.date : safeDayjs(dayScheduleData.date);
                                                    const dayStructure = dayScheduleStructures.find(s => s.key === dayScheduleData.key) || {};

                                                    return (
                                                        <div key={dayField.key} className="daily-schedule-item">
                                                            <Row justify="space-between" align="middle" style={{ marginBottom: '8px', padding: '5px', background: dayStructure.isWeekend ? '#fffbe6' : '#f9f9f9', borderRadius: '4px' }}>
                                                                <Col><Text strong>{dayScheduleDateDayjs && dayScheduleDateDayjs.isValid() ? dayScheduleDateDayjs.format('dddd, MMM D, YYYY') : 'Invalid Date'}</Text>{dayStructure.isWeekend && <Text type="warning" style={{ marginLeft: '8px' }}>(Weekend - No Slots)</Text>}</Col>
                                                                {!dayStructure.isWeekend && !isViewMode && (<Col><Button type="dashed" onClick={() => {
                                                                    const allSchedules = form.getFieldValue('dailyExamSchedules') || [];
                                                                    const updatedAllSchedules = JSON.parse(JSON.stringify(allSchedules));
                                                                    if (updatedAllSchedules[dayIndex]) {
                                                                        const targetDaySchedule = updatedAllSchedules[dayIndex];
                                                                        if (!targetDaySchedule.timeSlots) targetDaySchedule.timeSlots = [];
                                                                        targetDaySchedule.timeSlots.push({
                                                                            startTime: null, endTime: null, roomId: null,
                                                                            maxCapacity: 10, status: 'Available',
                                                                            key: `new-slot-${Date.now()}-${Math.random()}`
                                                                        });
                                                                        form.setFieldsValue({ dailyExamSchedules: updatedAllSchedules });
                                                                    }
                                                                }} icon={<PiPlusCircle />} size="small"> Add Slot </Button></Col>)}
                                                            </Row>
                                                            <Form.Item name={[dayField.name, 'date']} noStyle >
                                                                <Input type="hidden" />
                                                            </Form.Item>

                                                            {!dayStructure.isWeekend && (
                                                                <Form.List name={[dayField.name, 'timeSlots']}>
                                                                    {(timeSlotFields, timeSlotOperations) =>
                                                                        timeSlotFields.map((slotField, slotIndex) => {
                                                                            const slotData = form.getFieldValue(['dailyExamSchedules', dayIndex, 'timeSlots', slotIndex]);
                                                                            const roomNameForView = isViewMode && slotData?.roomId ? getRoomNameById(slotData.roomId) : null;
                                                                            const currentRoomForValidation = availableRooms.find(r => r._id === (slotData?.roomId));

                                                                            return (
                                                                                <div key={slotField.key} className="time-slot-item" style={{ borderBottom: '1px dashed #e8e8e8', marginBottom: '10px', paddingBottom: '10px' }}>
                                                                                    <Row gutter={16} align="middle" style={{ marginBottom: '8px' }}>
                                                                                        <Col xs={24} sm={24} md={8}>
                                                                                            <Form.Item
                                                                                                label="Room" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                                                                name={[slotField.name, 'roomId']}
                                                                                                rules={isViewMode ? [] : [{ required: true, message: 'Room required.' }]}
                                                                                            >
                                                                                                {isViewMode ? <Input value={roomNameForView || 'N/A'} disabled /> :
                                                                                                    <Select
                                                                                                        placeholder="Select Room"
                                                                                                        style={{ width: '100%' }}
                                                                                                        disabled={loadingRooms || loading}
                                                                                                        loading={loadingRooms}
                                                                                                        showSearch
                                                                                                        filterOption={(input, option) => (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())}
                                                                                                        onChange={(value) => handleRoomChangeForSlot(dayIndex, slotIndex, value)}
                                                                                                    >
                                                                                                        {availableRooms.map(room => (<Option key={room._id} value={room._id}>{room.name} (Cap: {room.capacity})</Option>))}
                                                                                                    </Select>
                                                                                                }
                                                                                            </Form.Item>
                                                                                        </Col>
                                                                                        <Col xs={12} sm={12} md={8}>
                                                                                            <Form.Item
                                                                                                label="Start Time" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                                                                name={[slotField.name, 'startTime']}
                                                                                                rules={isViewMode ? [] : [{ required: true, message: 'Required' }]}
                                                                                            >
                                                                                                <TimePicker style={{ width: '100%' }} format={TIME_FORMAT} minuteStep={15} disabledTime={disabledTime} disabled={isViewMode || loading} />
                                                                                            </Form.Item>
                                                                                        </Col>
                                                                                        <Col xs={12} sm={12} md={8}>
                                                                                            <Form.Item
                                                                                                label="End Time" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                                                                name={[slotField.name, 'endTime']}
                                                                                                rules={isViewMode ? [] : [{ required: true, message: 'Required' }, ({ getFieldValue }) => ({ validator(_, value) { const startTimeValue = getFieldValue(['dailyExamSchedules', dayIndex, 'timeSlots', slotIndex, 'startTime']); if (!value || !startTimeValue || !dayjs.isDayjs(value) || !dayjs.isDayjs(startTimeValue) || value.isAfter(startTimeValue)) return Promise.resolve(); return Promise.reject(new Error('End > Start!')); } })]}
                                                                                            >
                                                                                                <TimePicker style={{ width: '100%' }} format={TIME_FORMAT} minuteStep={15} disabledTime={disabledTime} disabled={isViewMode || loading} />
                                                                                            </Form.Item>
                                                                                        </Col>
                                                                                    </Row>
                                                                                    <Row gutter={16} align="middle">
                                                                                        <Col xs={12} sm={12} md={8}>
                                                                                            <Form.Item
                                                                                                label="Slot Capacity" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                                                                name={[slotField.name, 'maxCapacity']}
                                                                                                initialValue={10}
                                                                                                rules={isViewMode ? [] : [
                                                                                                    { required: true, message: 'Required' },
                                                                                                    { type: 'number', min: 0, message: 'Min 0.' },
                                                                                                    {
                                                                                                        validator: async (_, value) => {
                                                                                                            if (currentRoomForValidation && value !== undefined && value !== null && value > currentRoomForValidation.capacity) {
                                                                                                                return Promise.reject(new Error(`Max ${currentRoomForValidation.capacity}!`));
                                                                                                            }
                                                                                                            return Promise.resolve();
                                                                                                        }
                                                                                                    }
                                                                                                ]}
                                                                                            >
                                                                                                <InputNumber
                                                                                                    style={{ width: '100%' }}
                                                                                                    min={0}
                                                                                                    disabled={isViewMode || loading || !slotData?.roomId}
                                                                                                    placeholder={slotData?.roomId ? `Room Cap: ${currentRoomForValidation?.capacity || '?'}` : "Select Room"}
                                                                                                />
                                                                                            </Form.Item>
                                                                                        </Col>
                                                                                        <Col xs={12} sm={12} md={8}>
                                                                                            <Form.Item
                                                                                                label="Slot Status" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                                                                name={[slotField.name, 'status']}
                                                                                                initialValue="Available"
                                                                                                rules={isViewMode ? [] : [{ required: true }]}
                                                                                            >
                                                                                                <Select disabled={isViewMode || loading} style={{ width: '100%' }}>
                                                                                                    <Select.Option value="Available">Available</Select.Option>
                                                                                                    <Select.Option value="Unavailable">Unavailable</Select.Option>
                                                                                                </Select>
                                                                                            </Form.Item>
                                                                                        </Col>
                                                                                        {!isViewMode && (
                                                                                            <Col xs={24} sm={24} md={8} style={{ display: 'flex', alignItems: 'center', paddingTop: '8px', justifyContent: 'flex-start' }}>
                                                                                                <Popconfirm title="Remove this slot?" onConfirm={() => timeSlotOperations.remove(slotIndex)} okText="Yes" cancelText="No">
                                                                                                    <AntLink><PiTrash style={{ color: 'red', fontSize: '1.4em' }} /></AntLink>
                                                                                                </Popconfirm>
                                                                                            </Col>
                                                                                        )}
                                                                                    </Row>
                                                                                </div>
                                                                            );
                                                                        })
                                                                    }
                                                                </Form.List>
                                                            )}
                                                            {(dayScheduleData.timeSlots?.length === 0 && !dayStructure.isWeekend && !isViewMode) && <p style={{ color: '#888', fontSize: '0.9em', marginLeft: '5px' }}>No time slots. Add or use template.</p>}
                                                        </div>
                                                    );
                                                });
                                            }}
                                        </Form.List>
                                    </div>
                                </div>
                            </div>
                            {!isViewMode && (<div className="buttons" style={{ marginTop: 24, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}> <Button type="default" htmlType="button" onClick={handleBack} disabled={loading || initialDataLoading}> Cancel </Button> <Button type="primary" htmlType="submit" className={isCreateMode ? 'create-btn' : ''} loading={loading || initialDataLoading} style={{ marginLeft: 8 }} > {isEditMode ? 'Update Term' : 'Save Term'} </Button> </div>)}
                        </Form>
                    )}
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default CreateAcademicTerm;